/**
 * useSettings — reads the global `platformSettings/global` doc via the repo
 * (which merges over typed defaults so every field is always present). One-shot
 * read on mount; the motd/featured list rarely change mid-session.
 */
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_PLATFORM_SETTINGS, type PlatformSettings } from '@djgg/creations';
import { useServices } from '../services/context.js';

export interface SettingsState {
  settings: PlatformSettings;
  /** True once the one-shot read has settled (defaults on failure). Lets pages
   *  hold a loading state instead of painting defaults and reflowing when the
   *  motd/featured list arrive. */
  loaded: boolean;
  /** Present when the durable read failed. Noncritical pages may still use
   * defaults; authority-sensitive creation pages must fail closed. */
  error: string | null;
  /** Retry the durable read without remounting the page. */
  reload: () => void;
}

export function useSettings(): SettingsState {
  const services = useServices();
  const [state, setState] = useState<SettingsState>({
    settings: DEFAULT_PLATFORM_SETTINGS,
    loaded: false,
    error: null,
    reload: () => {},
  });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => {
    setState((current) => ({ ...current, loaded: false, error: null }));
    setAttempt((current) => current + 1);
  }, []);
  useEffect(() => {
    let active = true;
    // A settings read can fail (offline / rules); degrade to the typed defaults
    // rather than throwing an unhandled rejection — the motd/featured list are
    // non-critical gallery chrome.
    services.repos.settings
      .get()
      .then((s) => {
        if (active) setState({ settings: s, loaded: true, error: null, reload });
      })
      .catch((error: unknown) => {
        if (active) setState({
          settings: DEFAULT_PLATFORM_SETTINGS,
          loaded: true,
          error: error instanceof Error ? error.message : 'Could not load platform settings.',
          reload,
        });
      });
    return () => {
      active = false;
    };
  }, [attempt, reload, services]);
  return { ...state, reload };
}
